import React, { useEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";

/**
 * TourController — drives a guided camera tour around every marker.
 *
 *   - Lives inside the <Canvas>. Reads/writes a mutable `tourRef` shared
 *     with the parent ZoneScene; advances per-frame via useFrame.
 *   - Phases: "moving"  → lerp camera+target to the next marker (smoothstep)
 *             "lingering" → hold for ~3s with the description panel open.
 *   - When all markers have been visited, calls onComplete() and stops.
 *
 * tourRef contract (set up by the parent before flipping `active` to true):
 *   {
 *     active: bool,
 *     index: number,
 *     phase: "moving" | "lingering",
 *     phaseStart: number,    // useFrame elapsedTime when this phase began
 *     fromPos: Vector3,      // camera.position at the start of the move
 *     fromTarget: Vector3,   // orbit target at the start of the move
 *     transitionTime: number,// seconds per fly-between
 *     lingerTime: number,    // seconds to hold at each marker
 *   }
 */
export default function TourController({
  tourRef,
  controlsRef,
  markers,
  onArrive,
  onComplete,
}) {
  const { camera } = useThree();

  // Scratch vectors reused across frames.
  const scratch = React.useMemo(
    () => ({
      eye: new THREE.Vector3(),
      look: new THREE.Vector3(),
      lerpTarget: new THREE.Vector3(),
    }),
    []
  );

  // Compute a sensible camera "eye" for a marker — pull back along the
  // radial vector from the quad's centre so we always look INWARD at the
  // marker (avoids slicing through buildings on the far edges).
  function computeEye(marker, out) {
    const [x, y, z] = marker.position;
    let dx = x;
    let dz = z;
    const len = Math.hypot(dx, dz);
    if (len < 0.5) {
      // Markers near the centre — pick a soft south-east default.
      dx = 0.5;
      dz = 0.866;
    } else {
      dx /= len;
      dz /= len;
    }
    out.set(x + dx * 9, y + 5, z + dz * 9);
    return out;
  }

  useFrame((state) => {
    const tour = tourRef.current;
    if (!tour.active) return;
    const t = state.clock.elapsedTime;

    // First frame after activation — capture the live camera + orbit target
    // so the lerp starts from the user's actual viewpoint.
    if (tour.fromPos === null) {
      tour.fromPos = camera.position.clone();
      tour.fromTarget =
        controlsRef && controlsRef.current && controlsRef.current.target
          ? controlsRef.current.target.clone()
          : new THREE.Vector3(0, 2, 0);
      tour.phaseStart = t;
      tour.phase = "moving";
    }

    const elapsed = t - tour.phaseStart;

    const idx = tour.index;
    const marker = markers[idx];
    if (!marker) {
      tour.active = false;
      onComplete && onComplete();
      return;
    }

    const lookAt = scratch.look.set(...marker.position);
    const eyeTarget = computeEye(marker, scratch.eye);

    if (tour.phase === "moving") {
      const k = Math.min(1, elapsed / Math.max(0.01, tour.transitionTime));
      const eased = k * k * (3 - 2 * k); // smoothstep
      camera.position.lerpVectors(tour.fromPos, eyeTarget, eased);
      camera.up.set(0, 1, 0);
      if (controlsRef && controlsRef.current && controlsRef.current.target) {
        controlsRef.current.target
          .copy(tour.fromTarget)
          .lerp(lookAt, eased);
        if (controlsRef.current.update) controlsRef.current.update();
      } else {
        camera.lookAt(scratch.lerpTarget.copy(tour.fromTarget).lerp(lookAt, eased));
      }
      if (k >= 1) {
        tour.phase = "lingering";
        tour.phaseStart = t;
        onArrive && onArrive(marker);
      }
    } else if (tour.phase === "lingering") {
      if (elapsed >= tour.lingerTime) {
        const next = idx + 1;
        if (next >= markers.length) {
          tour.active = false;
          onComplete && onComplete();
          return;
        }
        tour.index = next;
        tour.phase = "moving";
        tour.phaseStart = t;
        tour.fromPos = camera.position.clone();
        tour.fromTarget =
          controlsRef && controlsRef.current && controlsRef.current.target
            ? controlsRef.current.target.clone()
            : new THREE.Vector3();
      }
    }
  });

  // Tour-active cursor — gentle visual cue.
  useEffect(() => {
    if (!tourRef.current.active) return;
    document.body.style.cursor = "progress";
    return () => {
      document.body.style.cursor = "auto";
    };
  });

  return null;
}
